import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { handlePossibleNonFieldError } from 'api/query-client';

import {
  addJourneyFormToPortal,
  createPortal,
  CreatePortalFormData,
  createPortalLink,
  deletePortal,
  deletePortalLink,
  editPortalDomain,
  loadPortal,
  loadPortalDomain,
  loadPortalLink,
  loadPortals,
  loadPortalSecurity,
  loadPortalStatistics,
  loadWorkspaceJourneyForms,
  PortalStatisticsFilters,
  removeJourneyFormFromPortal,
  updatePortal,
  updatePortalItemOrder,
  updatePortalLink,
  updatePortalSecurity,
  UpdatePortalSecurityData,
} from './requests';

const portalsQueryKey = 'portals';
const getPortalQueryKey = (portalShortId: string) => ['portal', portalShortId];
const getPortalItemsQueryKey = (portalShortId: string) => ['portal-items', portalShortId];
const getPortalLinkQueryKey = (portalShortId: string, linkShortId: string) => [
  'portal-link',
  portalShortId,
  linkShortId,
];
const getWorkspaceJourneyFormsQueryKey = (portalShortId: string) => [
  'workspace-journey-forms',
  portalShortId,
];

const useInvalidatePortals = () => {
  const queryClient = useQueryClient();

  const invalidatePortals = () => {
    queryClient.invalidateQueries([portalsQueryKey]);
  };

  const invalidatePortal = (portalShortId: string) => {
    queryClient.invalidateQueries(getPortalQueryKey(portalShortId));
  };

  const invalidatePortalItems = (portalShortId: string) => {
    queryClient.invalidateQueries(getPortalItemsQueryKey(portalShortId));
  };

  const invalidatePortalLink = (portalShortId: string, linkShortId: string) => {
    queryClient.invalidateQueries(getPortalLinkQueryKey(portalShortId, linkShortId));
  };

  const invalidateWorkspaceJourneyForms = (portalShortId: string) => {
    queryClient.invalidateQueries(getWorkspaceJourneyFormsQueryKey(portalShortId));
  };

  return {
    invalidatePortals,
    invalidatePortal,
    invalidatePortalLink,
    invalidatePortalItems,
    invalidateWorkspaceJourneyForms,
  };
};

export const usePortals = () => {
  return useQuery({
    queryKey: [portalsQueryKey],
    queryFn: loadPortals,
  });
};

export const usePortal = (portalShortId: string) => {
  return useQuery({
    queryKey: getPortalQueryKey(portalShortId),
    queryFn: () => loadPortal(portalShortId),
  });
};

export const useCreatePortal = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidatePortals } = useInvalidatePortals();

  return useMutation({
    mutationFn: createPortal,
    onSuccess: () => {
      invalidatePortals();
      showSuccess(t('newPortalCreated'));
    },
  });
};

type PortalEditErrorResponse = AxiosError<{
  name: string[];
}>;

export type PortalEditErrors = {
  name?: string;
};

export const useUpdatePortal = (
  portalShortId: string,
  options?: {
    onSuccessText?: string;
    onSuccess?: () => void;
  },
  setErrors?: (errors: PortalEditErrors) => void
) => {
  const { invalidatePortal, invalidatePortals } = useInvalidatePortals();
  return useMutation({
    mutationFn: (data: FormData | Record<string, any>) => updatePortal(portalShortId, data),
    onSuccess: () => {
      invalidatePortal(portalShortId);
      invalidatePortals();

      if (options?.onSuccessText) {
        showSuccess(options.onSuccessText);
      }

      options?.onSuccess?.();
    },
    onError: (data: PortalEditErrorResponse) => {
      if (setErrors) {
        const responseData = data?.response?.data as Record<string, string[]>;
        const nameErrors = responseData?.name;
        setErrors({ name: nameErrors?.[0] });
      }
    },
  });
};

export const useDeletePortal = () => {
  const { invalidatePortals } = useInvalidatePortals();
  return useMutation({
    mutationFn: deletePortal,
    onSuccess: invalidatePortals,
  });
};

export const useUpdatePortalItemOrder = (portalShortId: string) => {
  const { invalidatePortal } = useInvalidatePortals();
  return useMutation({
    mutationFn: (data: Parameters<typeof updatePortalItemOrder>[1]) =>
      updatePortalItemOrder(portalShortId, data),
    onSuccess: () => {
      invalidatePortal(portalShortId);
    },
  });
};

export const usePortalLink = (portalShortId: string, linkShortId: string) => {
  return useQuery({
    queryKey: getPortalLinkQueryKey(portalShortId, linkShortId),
    queryFn: () => loadPortalLink(portalShortId, linkShortId),
    enabled: Boolean(portalShortId && linkShortId && linkShortId.trim() !== ''),
  });
};

export const useCreatePortalLink = (portalShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidatePortal } = useInvalidatePortals();

  return useMutation({
    mutationFn: (data: FormData) => createPortalLink(portalShortId, data),
    onSuccess: () => {
      invalidatePortal(portalShortId);
      showSuccess(t('newLinkCreated'));
    },
  });
};

export const useUpdatePortalLink = (portalShortId: string, linkShortId: string) => {
  const { invalidatePortal, invalidatePortalLink } = useInvalidatePortals();

  return useMutation({
    mutationFn: (data: FormData) => updatePortalLink(portalShortId, linkShortId, data),
    onSuccess: () => {
      invalidatePortalLink(portalShortId, linkShortId);
      invalidatePortal(portalShortId);
    },
  });
};

export const useDeletePortalLink = (portalShortId: string) => {
  const { invalidatePortal, invalidatePortalItems } = useInvalidatePortals();

  return useMutation({
    mutationFn: (linkShortId: string) => deletePortalLink(portalShortId, linkShortId),
    onSuccess: () => {
      invalidatePortal(portalShortId);
      invalidatePortalItems(portalShortId);
    },
  });
};

export const useWorkspaceJourneyForms = (portalShortId: string) => {
  return useQuery({
    queryKey: ['workspace-journey-forms', portalShortId],
    queryFn: () => loadWorkspaceJourneyForms(portalShortId),
  });
};

export const useAddJourneyFormToPortal = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidatePortal, invalidateWorkspaceJourneyForms } = useInvalidatePortals();
  return useMutation({
    mutationFn: (data: CreatePortalFormData & { portalShortId: string }) =>
      addJourneyFormToPortal(data.portalShortId, { form: data.form }),
    onSuccess: (_, variables) => {
      invalidatePortal(variables.portalShortId);
      invalidateWorkspaceJourneyForms(variables.portalShortId);
      showSuccess(t('externalFormAdded'));
    },
  });
};

export const useRemoveJourneyFormFromPortal = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidatePortal, invalidateWorkspaceJourneyForms } = useInvalidatePortals();

  return useMutation({
    mutationFn: (data: {
      portalShortId: string;
      formShortId: string;
      useJourneyFormId?: boolean;
    }) => removeJourneyFormFromPortal(data.portalShortId, data.formShortId, data.useJourneyFormId),
    onSuccess: (_, variables) => {
      invalidatePortal(variables.portalShortId);
      invalidateWorkspaceJourneyForms(variables.portalShortId);
      showSuccess(t('externalFormRemoved'));
    },
  });
};

export const usePortalStatistics = (filters: PortalStatisticsFilters) => {
  return useQuery({
    queryKey: ['portal-statistics', filters],
    queryFn: () => loadPortalStatistics(filters),
  });
};

const portalDomainQueryKey = (portalShortId: string) => ['company-portal-domain', portalShortId];

export const usePortalDomain = (portalShortId: string) => {
  return useQuery({
    queryKey: portalDomainQueryKey(portalShortId),
    queryFn: () => loadPortalDomain(portalShortId),
  });
};

export const useEditPortalDomain = (portalShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { domain: string }) => editPortalDomain(portalShortId, data),
    onSuccess: async () => {
      showSuccess(t('portalDomainEditSuccess'));
      await queryClient.refetchQueries(portalDomainQueryKey(portalShortId));
      queryClient.invalidateQueries(['company-details']);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('portalDomainEditError'));
    },
  });
};

const portalSecurityQueryKey = (portalShortId: string) => ['portal-security', portalShortId];

export const usePortalSecurity = (portalShortId: string) => {
  return useQuery({
    queryKey: portalSecurityQueryKey(portalShortId),
    queryFn: () => loadPortalSecurity(portalShortId),
  });
};

export const useUpdatePortalSecurity = (portalShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { invalidatePortal } = useInvalidatePortals();

  return useMutation({
    mutationFn: (data: UpdatePortalSecurityData) => updatePortalSecurity(portalShortId, data),
    onSuccess: async () => {
      showSuccess(t('securitySettingsUpdated'));
      await queryClient.refetchQueries(portalSecurityQueryKey(portalShortId));
      invalidatePortal(portalShortId);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('securitySettingsUpdateError'));
    },
  });
};

const locale = {
  en: {
    newPortalCreated: 'New portal created',
    newLinkCreated: 'New custom link created',
    portalDomainEditError: 'Domain already registered, please use a unique domain',
    portalDomainEditSuccess:
      'Domain saved. Please follow the instructions on the page to set up the DNS.',
    externalFormRemoved: 'External form removed from portal',
    externalFormAdded: 'External form added to portal',
    securitySettingsUpdated: 'Security settings updated',
    securitySettingsUpdateError: 'Failed to update security settings',
  },
};
